// TermsOfService.tsx
import React from 'react'
import { Helmet } from 'react-helmet-async'
import { Shield, FileText } from 'lucide-react'

interface Props { dark: boolean; apiBase: string; productUrl: string }

const Section: React.FC<{ title: string; children: React.ReactNode; dark: boolean }> = ({ title, children, dark }) => (
  <section className="mb-10">
    <h2 className={`text-xl font-bold mb-4 ${dark ? 'text-white' : 'text-gray-900'}`}>{title}</h2>
    <div className={`text-base leading-relaxed space-y-3 ${dark ? 'text-gray-300' : 'text-gray-700'}`}>
      {children}
    </div>
  </section>
)

const TermsOfService: React.FC<Props> = ({ dark }) => {
  const updated = 'January 1, 2026'

  return (
    <>
      <Helmet>
        <title>Larxnet | Terms of Service</title>
        <meta name="description" content="Larxnet Terms of Service. Please read these terms carefully before using our platform." />
      </Helmet>

      <div className={`min-h-screen py-20 page-enter ${dark ? 'text-white' : 'text-gray-900'}`}>
        <div className="max-w-3xl mx-auto px-6">
          {/* Header */}
          <div className="mb-14 text-center">
            <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-6 ${dark ? 'bg-blue-500/10' : 'bg-blue-50'}`}>
              <FileText className="w-8 h-8 text-blue-400" />
            </div>
            <h1 className="text-4xl md:text-5xl font-black mb-4">Terms of Service</h1>
            <p className={`text-sm ${dark ? 'text-gray-400' : 'text-gray-500'}`}>
              Last updated: {updated}
            </p>
          </div>

          {/* Content */}
          <div className={`rounded-3xl border p-8 md:p-12 ${dark ? 'bg-gray-900/50 border-gray-800' : 'bg-white border-gray-200 shadow-sm'}`}>

            <Section title="1. Acceptance of Terms" dark={dark}>
              <p>By accessing or using Larxnet ("the Platform"), you agree to be bound by these Terms of Service and all applicable laws and regulations. If you do not agree with any part of these terms, you may not use our services.</p>
              <p>These terms apply to all visitors, users, and others who access or use the Platform.</p>
            </Section>

            <Section title="2. Description of Service" dark={dark}>
              <p>Larxnet provides enterprise-grade data management and agentic analytics services, including but not limited to real-time market signals, AI-powered analytics, technical indicators, and data management tools ("Services").</p>
              <p>We reserve the right to modify, suspend, or discontinue the Services at any time without notice.</p>
            </Section>

            <Section title="3. User Accounts" dark={dark}>
              <p>To access certain features, you must create an account. You are responsible for:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Maintaining the confidentiality of your account credentials</li>
                <li>All activities that occur under your account</li>
                <li>Providing accurate and current information during registration</li>
                <li>Notifying us immediately of any unauthorized access</li>
              </ul>
            </Section>

            <Section title="4. Acceptable Use" dark={dark}>
              <p>You agree not to use the Platform to:</p>
              <ul className="list-disc list-inside space-y-1 ml-4">
                <li>Violate any applicable laws or regulations</li>
                <li>Engage in market manipulation or illegal trading activity</li>
                <li>Attempt to gain unauthorized access to any system or network</li>
                <li>Transmit malware, viruses, or harmful code</li>
                <li>Scrape, crawl, or harvest data without permission</li>
                <li>Interfere with other users' access to the Platform</li>
              </ul>
            </Section>

            <Section title="5. Financial Disclaimer" dark={dark}>
              <p>The information and signals provided by Larxnet are for informational and educational purposes only. Nothing on the Platform constitutes financial, investment, or trading advice.</p>
              <p>Past performance is not indicative of future results. Trading in financial markets involves significant risk of loss. You should consult a qualified financial advisor before making investment decisions.</p>
            </Section>

            <Section title="6. Intellectual Property" dark={dark}>
              <p>All content, features, and functionality of the Platform — including text, graphics, logos, algorithms, and software — are owned by Larxnet and are protected by intellectual property laws.</p>
              <p>You may not reproduce, distribute, or create derivative works without our express written permission.</p>
            </Section>

            <Section title="7. Data and Privacy" dark={dark}>
              <p>Your use of the Platform is also governed by our Privacy Policy, which is incorporated into these Terms by reference. By using the Platform, you consent to the practices described in our Privacy Policy.</p>
            </Section>

            <Section title="8. Limitation of Liability" dark={dark}>
              <p>To the fullest extent permitted by law, Larxnet shall not be liable for any indirect, incidental, special, consequential, or punitive damages, including but not limited to loss of profits, data, or goodwill resulting from your use of the Platform.</p>
              <p>Our total liability for any claim arising out of or relating to these Terms or the Platform shall not exceed the amount paid by you in the twelve months preceding the claim.</p>
            </Section>

            <Section title="9. Indemnification" dark={dark}>
              <p>You agree to indemnify, defend, and hold harmless Larxnet and its affiliates, officers, agents, and employees from any claim, liability, damage, or expense (including reasonable attorneys' fees) arising from your use of the Platform or violation of these Terms.</p>
            </Section>

            <Section title="10. Termination" dark={dark}>
              <p>We may terminate or suspend your account at any time, with or without cause and without prior notice, if we believe you have violated these Terms or for any other business reason.</p>
              <p>Upon termination, your right to use the Platform will immediately cease.</p>
            </Section>

            <Section title="11. Changes to Terms" dark={dark}>
              <p>We reserve the right to update these Terms at any time. We will notify users of material changes by updating the "Last updated" date. Continued use of the Platform after changes constitutes acceptance of the new Terms.</p>
            </Section>

            <Section title="12. Governing Law" dark={dark}>
              <p>These Terms shall be governed by and construed in accordance with applicable law, without regard to conflict of law principles. Any disputes shall be resolved through binding arbitration or in courts of competent jurisdiction.</p>
            </Section>

            <Section title="13. Contact" dark={dark}>
              <p>For questions about these Terms, please contact us through our <a href="/contact" className="text-blue-400 hover:text-blue-300 underline">Contact page</a>.</p>
            </Section>
          </div>
        </div>
      </div>
    </>
  )
}

export default TermsOfService
